import React, { useState, useEffect } from 'react';
import { Download, Search, User, Mail, Phone, Building } from 'lucide-react';

const RegistrationsTab = ({ registrations, event, loading, userDetails = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRegistration, setExpandedRegistration] = useState(null);
  
  // Debug logging
  useEffect(() => {
    console.log('RegistrationsTab - Event:', event);
    console.log('RegistrationsTab - Registrations:', registrations);
    console.log('RegistrationsTab - Loading:', loading);
    console.log('RegistrationsTab - User Details:', userDetails);
    
    // Check if registrations have the expected structure
    if (registrations && registrations.length > 0) {
      const firstReg = registrations[0];
      console.log('First registration structure:', {
        hasStudioName: !!firstReg.studioName,
        hasContactName: !!firstReg.contactName,
        hasEmail: !!firstReg.email,
        hasDancers: Array.isArray(firstReg.dancers),
        dancersLength: firstReg.dancers?.length || 0,
        hasTeachers: Array.isArray(firstReg.teachers),
        teachersLength: firstReg.teachers?.length || 0,
        hasObservers: Array.isArray(firstReg.observers),
        observersLength: firstReg.observers?.length || 0,
        hasCompetition: !!firstReg.competition,
        hasRoutines: Array.isArray(firstReg.competition?.routines),
        routinesLength: firstReg.competition?.routines?.length || 0,
        registrationId: firstReg.registrationId,
        userId: firstReg.userId,
        fullObject: firstReg // Log the full object for inspection
      });
    }
  }, [registrations, event, loading, userDetails]);
  
  // Helper function to get user's full name
  const getUserFullName = (registration) => {
    // First try to use the user details stored directly in the registration
    if (registration.userFirstName || registration.userLastName) {
      console.log(`Using user details from registration: ${registration.userFirstName} ${registration.userLastName}`);
      return `${registration.userFirstName || ''} ${registration.userLastName || ''}`.trim();
    }
    
    // Fall back to userId lookup if direct details aren't available
    const userId = registration.userId;
    if (!userId) return 'No User ID';
    
    console.log(`Getting full name for userId: ${userId}`);
    console.log(`User details for ${userId}:`, userDetails[userId]);
    
    const user = userDetails[userId];
    if (!user) {
      console.log(`No user details found for userId: ${userId}`);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Check if user is a string (might be an error message)
    if (typeof user === 'string') {
      console.log(`User details for ${userId} is a string:`, user);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Try to extract name from different possible structures
    let firstName = '';
    let lastName = '';
    let email = '';
    let name = '';
    
    // Direct properties
    if (user.firstName) firstName = user.firstName;
    if (user.lastName) lastName = user.lastName;
    if (user.email) email = user.email;
    if (user.name) name = user.name;
    if (user.contactName) name = user.contactName;
    
    // Nested properties
    if (user.user) {
      if (user.user.firstName) firstName = user.user.firstName;
      if (user.user.lastName) lastName = user.user.lastName;
      if (user.user.email) email = user.user.email;
      if (user.user.name) name = user.user.name;
    }
    
    // Attributes property (common in Cognito user objects)
    if (user.attributes) {
      if (user.attributes.given_name) firstName = user.attributes.given_name;
      if (user.attributes.family_name) lastName = user.attributes.family_name;
      if (user.attributes.email) email = user.attributes.email;
      if (user.attributes.name) name = user.attributes.name;
    }
    
    // Build the full name
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (name) {
      return name;
    } else if (email) {
      return email;
    }
    
    console.log(`Could not extract name for userId: ${userId}`);
    return `User ${userId.substring(0, 8)}...`;
  };
  
  // Filter registrations based on search term
  const filteredRegistrations = registrations.filter(reg => {
    const searchLower = searchTerm.toLowerCase();
    const userFullName = getUserFullName(reg).toLowerCase();
    
    return (
      (reg.studioName && reg.studioName.toLowerCase().includes(searchLower)) ||
      (reg.contactName && reg.contactName.toLowerCase().includes(searchLower)) ||
      (reg.email && reg.email.toLowerCase().includes(searchLower)) ||
      userFullName.includes(searchLower)
    );
  });
  
  // Simple fallback component for raw data display
  const RawDataDisplay = ({ title, data }) => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return <div className="p-4 bg-base-200 rounded-lg">No {title.toLowerCase()} data available</div>;
    }
    
    return (
      <div className="p-4 bg-base-200 rounded-lg">
        <h3 className="font-medium mb-2">{title} (Raw Data)</h3>
        <div className="overflow-x-auto">
          <pre className="text-xs bg-base-300 p-2 rounded max-h-60 overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };
  
  // Toggle expanded registration details
  const toggleExpand = (registrationId) => {
    if (expandedRegistration === registrationId) {
      setExpandedRegistration(null);
    } else {
      setExpandedRegistration(registrationId);
    }
  };
  
  // Export registrations as CSV
  const exportRegistrationsCSV = () => {
    if (!registrations.length) return;
    
    // Define CSV headers
    const headers = [
      'Studio Name',
      'Contact Name',
      'Email',
      'Phone',
      'Dancers',
      'Teachers',
      'Observers',
      'Routines',
      'Total Paid'
    ].join(',');
    
    // Create CSV rows
    const csvRows = registrations.map(reg => {
      const dancerCount = reg.dancers?.length || 0;
      const teacherCount = reg.teachers?.length || 0;
      const observerCount = reg.observers?.length || 0;
      const routineCount = reg.competition?.routines?.length || 0;
      const totalPaid = reg.pricing?.totalPaid || 0;
      
      return [
        `"${reg.studioName || 'Unknown Studio'}"`,
        `"${reg.contactName || ''}"`,
        `"${reg.email || ''}"`,
        `"${reg.phone || ''}"`,
        dancerCount,
        teacherCount,
        observerCount,
        routineCount,
        totalPaid
      ].join(',');
    });
    
    // Combine headers and rows
    const csvContent = [headers, ...csvRows].join('\n');
    
    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${event.city}_registrations.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header with search and export */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Search registrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
        </div>
        
        <button
          className="btn btn-primary btn-sm flex items-center gap-2"
          onClick={exportRegistrationsCSV}
          disabled={!registrations.length}
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>
      
      {/* Registrations count */}
      <div className="text-sm text-base-content/70">
        Showing {filteredRegistrations.length} of {registrations.length} registrations
      </div>
      
      {/* Raw data display for debugging */}
      <div className="bg-base-200 p-4 rounded-lg mb-4">
        <h3 className="font-medium mb-2">Debug Information</h3>
        <p>Loading: {loading ? 'Yes' : 'No'}</p>
        <p>Registrations count: {registrations.length}</p>
        <p>Filtered registrations count: {filteredRegistrations.length}</p>
        <p>User details count: {Object.keys(userDetails).length}</p>
        
        {registrations.length > 0 && (
          <div className="mt-2">
            <p className="font-medium">First Registration:</p>
            <pre className="bg-base-300 p-2 rounded text-xs mt-1 overflow-auto max-h-40">
              {JSON.stringify(registrations[0], null, 2)}
            </pre>
            
            {registrations[0]?.userId && userDetails[registrations[0].userId] && (
              <div className="mt-2">
                <p className="font-medium">User Details for {registrations[0].userId}:</p>
                <pre className="bg-base-300 p-2 rounded text-xs mt-1 overflow-auto max-h-40">
                  {JSON.stringify(userDetails[registrations[0].userId], null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Fallback display for when the UI components fail */}
      {registrations.length > 0 && (
        <div className="mb-4">
          <RawDataDisplay title="Registrations" data={registrations} />
        </div>
      )}
      
      {/* Registrations list */}
      {filteredRegistrations.length === 0 ? (
        <div className="text-center py-12 bg-base-200 rounded-lg">
          <p className="text-base-content/70">
            {registrations.length === 0
              ? 'No registrations found for this event'
              : 'No registrations match your search'}
          </p>
          
          {/* Debug information */}
          {registrations.length === 0 && (
            <div className="mt-4 text-left p-4 bg-base-300 rounded-lg max-w-2xl mx-auto">
              <h3 className="font-medium mb-2">Debug Information</h3>
              <p className="text-sm mb-2">Event ID: {event?.eventId || 'No event selected'}</p>
              <p className="text-sm mb-2">Event City: {event?.city || 'Unknown'}</p>
              <p className="text-sm">
                If you're seeing this message, it means no registrations were found for this event.
                Please check that:
              </p>
              <ul className="list-disc list-inside text-sm mt-2">
                <li>The event ID is correct</li>
                <li>There are registrations in the database for this event</li>
                <li>The API endpoint is correctly configured</li>
                <li>The registration data structure matches what the component expects</li>
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRegistrations.map((registration, index) => (
            <div
              key={registration.registrationId || `registration-${index}`}
              className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Registration header */}
              <div 
                className="card-body p-4 cursor-pointer"
                onClick={() => toggleExpand(registration.registrationId || `registration-${index}`)}
              >
                <div className="flex flex-col md:flex-row justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <Building className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">
                        {registration.studioName || 'Unknown Studio'}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-base-content/70">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{getUserFullName(registration)}</span>
                        </div>
                        {registration.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            <span>{registration.email}</span>
                          </div>
                        )}
                        {registration.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            <span>{registration.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-3 md:mt-0">
                    <div className="badge badge-primary">
                      {registration.dancers?.length || 0} {(registration.dancers?.length || 0) === 1 ? 'Dancer' : 'Dancers'}
                    </div>
                    <div className="badge badge-secondary">
                      {registration.teachers?.length || 0} {(registration.teachers?.length || 0) === 1 ? 'Teacher' : 'Teachers'}
                    </div>
                    <div className="badge badge-accent">
                      {registration.observers?.length || 0} {(registration.observers?.length || 0) === 1 ? 'Observer' : 'Observers'}
                    </div>
                    <div className="badge badge-info">
                      {registration.competition?.routines?.length || 0} {(registration.competition?.routines?.length || 0) === 1 ? 'Routine' : 'Routines'}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Expanded details */}
              {expandedRegistration === (registration.registrationId || `registration-${index}`) && (
                <div className="border-t border-base-300 p-4">
                  {/* Account Owner Information */}
                  <div className="mb-6 bg-primary/5 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Account Owner Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium">Name:</p>
                        <p className="text-base">{getUserFullName(registration)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">User ID:</p>
                        <p className="text-xs text-base-content/70">{registration.userId || 'N/A'}</p>
                      </div>
                      {registration.email && (
                        <div>
                          <p className="text-sm font-medium">Email:</p>
                          <p className="text-base">{registration.email}</p>
                        </div>
                      )}
                      {registration.phone && (
                        <div>
                          <p className="text-sm font-medium">Phone:</p>
                          <p className="text-base">{registration.phone}</p>
                        </div>
                      )}
                      {registration.studioName && (
                        <div>
                          <p className="text-sm font-medium">Studio:</p>
                          <p className="text-base">{registration.studioName}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Dancers */}
                    <div>
                      <h4 className="font-medium mb-2">Dancers</h4>
                      {registration.dancers && registration.dancers.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="table table-compact w-full">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Age</th>
                              </tr>
                            </thead>
                            <tbody>
                              {registration.dancers.map((dancer, idx) => (
                                <tr key={`dancer-${idx}`}>
                                  <td>
                                    {dancer.firstName && dancer.lastName 
                                      ? `${dancer.firstName} ${dancer.lastName}`
                                      : dancer.firstName || dancer.lastName || dancer.name || 'No name'}
                                  </td>
                                  <td>{dancer.age || 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-base-content/70">No dancers registered</p>
                      )}
                    </div>
                    
                    {/* Teachers */}
                    <div>
                      <h4 className="font-medium mb-2">Teachers</h4>
                      {registration.teachers && registration.teachers.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="table table-compact w-full">
                            <thead>
                              <tr>
                                <th>Name</th>
                              </tr>
                            </thead>
                            <tbody>
                              {registration.teachers.map((teacher, idx) => (
                                <tr key={`teacher-${idx}`}>
                                  <td>
                                    {teacher.firstName && teacher.lastName 
                                      ? `${teacher.firstName} ${teacher.lastName}`
                                      : teacher.firstName || teacher.lastName || teacher.name || 'No name'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-base-content/70">No teachers registered</p>
                      )}
                    </div>
                    
                    {/* Routines */}
                    <div>
                      <h4 className="font-medium mb-2">Routines</h4>
                      {registration.competition?.routines && registration.competition.routines.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="table table-compact w-full">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Category</th>
                              </tr>
                            </thead>
                            <tbody>
                              {registration.competition.routines.map((routine, idx) => (
                                <tr key={`routine-${idx}`}>
                                  <td>{routine.title || routine.name || 'Untitled Routine'}</td>
                                  <td>{routine.category || 'Uncategorized'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-base-content/70">No routines registered</p>
                      )}
                    </div>
                    
                    {/* Payment Info */}
                    <div>
                      <h4 className="font-medium mb-2">Payment Information</h4>
                      {registration.pricing ? (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Total Amount:</span>
                            <span className="font-medium">
                              ${((registration.pricing.totalAmount || 0) / 100).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Amount Paid:</span>
                            <span className="font-medium">
                              ${((registration.pricing.totalPaid || 0) / 100).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Balance Due:</span>
                            <span className="font-medium">
                              ${(((registration.pricing.totalAmount || 0) - (registration.pricing.totalPaid || 0)) / 100).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-base-content/70">No payment information available</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RegistrationsTab; 